// src/hooks/useAuth.js
import { useMutation } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";
import {
  setAuthFromLogin,
  logout,
  selectToken,
  selectUser,
  setPermissions,
} from "../store/authSlice";
import api from "../api/apiClient";
import { notify } from "../utils/notifications";
import extractApiError from "../api/extractApiError";

/**
 * تسجيل الدخول
 * توقّع استجابة مثل: { token: '...', user: {...} }
 */
export function useLogin() {
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: async ({ email, password }) => {
      const { data } = await api.post("/auth/login", { email, password });
      return data; // { token, user }
    },
    onSuccess: (data) => {
            const authData = {
        token: data.data.token,
        user: {
          ...data.data.user,
          permissions: [data.data.user.role] // نضيف الدور كصلاحية
        }
      };
      dispatch(setAuthFromLogin(authData));
      // interceptors في apiClient رح تضيف Authorization تلقائيًا بعد حفظ التوكن
    },
    onError: (error) => {
      notify(error, "error");
    },
  });
}

/**
 * تسجيل الخروج
 * يستدعي /auth/logout (إن وُجد) ثم ينظّف الحالة محليًا دائمًا
 */
export function useLogout() {
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: async () => {
      try {
        await api.post("/auth/logout"); // لو غير موجود، ما في مشكلة لو رجّع 404—رح ننظّف الحالة محليًا
      } catch {
        // نتجاهل الخطأ ونكمل تنظيف الحالة
      }
      return true;
    },
    onSettled: () => {
      dispatch(logout());
    },
    onError: (error) => {
      notify(extractApiError(error, "حدث خطأ في تسجيل الخروج"), "error");
    },
  });
}

/**
 * قراءات جاهزة من Redux (بدون طلبات شبكة)
 */
export function useAuthUser() {
  const user = useSelector(selectUser);
  const token = useSelector(selectToken);
  const isLoggedIn = !!token;
  return { user, token, isLoggedIn };
}

/**
 * تحديث صلاحيات المستخدم محليًا
 */
export function useSetPermissions() {
  const dispatch = useDispatch();
  return (permissions = []) => dispatch(setPermissions(permissions));
}

/**
 * نسيت كلمة المرور
 * POST /auth/forgot-password { email }
 */
export function useForgotPassword() {
  return useMutation({
    mutationFn: async ({ email }) => {
      const { data } = await api.post("/auth/forgot-password", { email });
      return data; // { message? }
    },
    onError: (error) => {
      notify(
        extractApiError(error, "حدث خطأ في إرسال رابط إعادة تعيين كلمة المرور"),
        "error"
      );
    },
  });
}

/**
 * إعادة تعيين كلمة المرور
 * POST /auth/reset-password { token, password }
 */
export function useResetPassword() {
  return useMutation({
    mutationFn: async ({ token, password }) => {
      const { data } = await api.post("/auth/reset-password", {
        token,
        password,
      });
      return data; // { message? }
    },
    onError: (error) => {
      notify(extractApiError(error, "حدث خطأ في إعادة تعيين كلمة المرور"), "error");
    },
  });
}
